import type { LandingPage } from './types'
import { getBasePageBySlug } from './registry'
import { generatedCombos } from './combos.generated'
import { comboPerexOverrides } from './comboPerexes'
import { comboAbouts } from './comboAbouts'

/**
 * Region×dimension combo landing pages (e.g. "Ubytování se saunou – Krkonoše",
 * "Chalupy – Šumava"). Which combos exist is decided at build time by catalog
 * inventory (see scripts/generate-landing-combos.ts → combos.generated.ts); the
 * page copy is composed here from the two base pages so runtime and build stay
 * in sync from a single committed data file.
 */

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** Lowercase noun phrase for a base page, used to compose combo copy. */
function comboLabel(base: LandingPage): string {
  return base.kind === 'category' || base.kind === 'label' ? base.linkLabel.toLowerCase() : base.h1.toLowerCase()
}

function synthesizeCombo(regionSlug: string, baseSlug: string): LandingPage | null {
  const region = getBasePageBySlug(regionSlug)
  const base = getBasePageBySlug(baseSlug)
  // region must be a region; base must be a dimension (facility/category), never
  // another region — rules out reversed/invalid paths like /s-krbem/jizni-cechy.
  if (!region || !base || region.kind !== 'region' || base.kind === 'region') {
    return null
  }

  const label = comboLabel(base)
  const regionName = region.linkLabel
  const h1 = `${capitalizeFirst(label)} – ${regionName}`

  return {
    slug: `${baseSlug}-${regionSlug}`,
    kind: 'combo',
    h1,
    linkLabel: h1,
    metaTitle: `${h1} | Mátevolno`,
    perex:
      comboPerexOverrides[`${regionSlug}/${baseSlug}`] ??
      `Vybírejte z aktuálních last-minute nabídek – ${label} v regionu ${regionName}. Prohlédněte si volné termíny a vyrazte na poslední chvíli bez dlouhého plánování.`,
    filter: { ...base.filter, region: regionSlug },
    comboBase: baseSlug,
    comboRegion: regionSlug,
    about: comboAbouts[`${regionSlug}/${baseSlug}`],
  }
}

// Lazily built on first use — avoids a top-level circular dependency with
// registry.ts (registry.getLandingPages() calls getComboPages(); by then
// registry's base pages are fully initialized).
let cache: LandingPage[] | null = null

export function getComboPages(): LandingPage[] {
  if (cache === null) {
    cache = generatedCombos
      .map((combo) => synthesizeCombo(combo.region, combo.base))
      .filter((page): page is LandingPage => page !== null)
  }
  return cache
}

/** Combos that involve a given base or region slug — used for interlinking. */
export function getCombosInvolving(slug: string): LandingPage[] {
  return getComboPages().filter((combo) => combo.comboBase === slug || combo.comboRegion === slug)
}

/**
 * Resolve a `/{region}/{dimension}` combo from its path parts. Permissive: any
 * valid region×dimension renders at runtime (returns null for invalid pairs),
 * while only the generated set (getComboPages) is indexed and interlinked.
 */
export function getComboByParts(regionSlug: string, baseSlug: string): LandingPage | null {
  return synthesizeCombo(regionSlug, baseSlug)
}

/**
 * Whether a combo is inventory-backed, i.e. part of the generated set that
 * belongs in the sitemap. Everything else renders for users but is kept out of
 * the index (`noindex`) and is never linked as a combo — such pages have no
 * offers of their own and would compete with the real landing pages in search.
 */
export function isIndexedCombo(regionSlug: string, baseSlug: string): boolean {
  return generatedCombos.some((combo) => combo.region === regionSlug && combo.base === baseSlug)
}
